import XDate from 'xdate'

import { ManagedBalanceSummary, ResidentTenancy } from '../_types/resident'
import { formatCents } from './resident-format'

/**
 * What a renter is told in place of a balance when their property manager keeps
 * the books somewhere else and no fresh figure from that system is available.
 * Shared by every surface that would otherwise show a figure or a pay button, so
 * they cannot say different things.
 */
const RENT_HANDLED_ELSEWHERE_MESSAGE =
  'Your property manager handles rent for this home outside QuickPass. Please contact them to pay or to check your balance.'

/**
 * How old a balance from the property manager's system may be and still be shown
 * to a renter. Past this it is withheld rather than shown as current: a renter
 * who paid yesterday must not be told they still owe it.
 */
const SOURCE_BALANCE_FRESHNESS_MS = 36 * 60 * 60 * 1000

/**
 * Whether this home's rent and balance are kept by the property manager outside
 * QuickPass. When they are, the ledger here is not the renter's real balance: an
 * empty one would read as nothing owing, so it is not shown and no rent is taken
 * against it.
 *
 * Tested positively, so a tenancy from a server that does not send the keeper
 * yet behaves exactly as it always has.
 *
 * @param {ResidentTenancy | null} tenancy - the selected tenancy, if any
 * @returns {boolean} true when the books are kept outside QuickPass
 */
const isRentHandledElsewhere = (tenancy: ResidentTenancy | null): boolean =>
  tenancy !== null &&
  typeof tenancy.ledgerKeeper === 'string' &&
  tenancy.ledgerKeeper !== 'quickcasa'

/**
 * The balance the property manager's own system reported, shaped for display,
 * when there is a fresh one to show. Null when the books are kept by QuickPass,
 * when that system does not track a balance for this home, or when the figure is
 * too old to trust, in which case the surface falls back to
 * RENT_HANDLED_ELSEWHERE_MESSAGE.
 *
 * @param {ResidentTenancy | null} tenancy - the selected tenancy, if any
 * @param {number} now - the current time in milliseconds
 * @returns {ManagedBalanceSummary | null} what to show, or null
 */
const resolveManagedBalanceSummary = (
  tenancy: ResidentTenancy | null,
  now: number,
): ManagedBalanceSummary | null => {
  if (tenancy === null || isRentHandledElsewhere(tenancy) === false) {
    return null
  }

  const balance = tenancy.sourceBalance ?? null
  const lastSyncedAt = tenancy.lastSyncedAt

  if (
    balance === null ||
    balance.isTracked !== true ||
    typeof balance.owedTodayCents !== 'number' ||
    typeof lastSyncedAt !== 'number' ||
    now - lastSyncedAt > SOURCE_BALANCE_FRESHNESS_MS
  ) {
    return null
  }

  const owedCents = balance.owedTodayCents

  return {
    label: owedCents < 0 ? 'Credit on your account' : 'Balance owing',
    amountText: formatCents(Math.abs(owedCents)),
    noteText: `As of ${new XDate(lastSyncedAt).toString(
      'MMM d, h:mm TT',
    )}. Your property manager handles rent for this home outside QuickPass, so please pay them directly.`,
  }
}

export {
  RENT_HANDLED_ELSEWHERE_MESSAGE,
  isRentHandledElsewhere,
  resolveManagedBalanceSummary,
}
